import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useFetchAthleteQuery } from "../../store";
import { 
  calculateAllParameters,
  generateCalendarLines,
  organizeLinesByMonth,
} from "./wizard/services/CalculationService";

// Hardcoded config for now
const MOCK_CONFIG = {
  goal: "bulk",
  bulkConfig: {
    morningWeight: "74.5",
    nightWeight: "76",
    currentFatPercentage: "9.5",
    targetFatPercentage: "13.5",
    leanMassGain: "1.7",
    rhythm: "weekly",
    time: 17,
    skinFolds: [
      {
        name: "Flotador",
        current: "16",
        target: "28"
      },
      {
        name: "Culo",
        current: "10",
        target: "22"
      }
    ],
    perimeters: [
      {
        name: "Cintura",
        current: "82",
        target: "90"
      },
      {
        name: "Biceps",
        current: "36.5",
        target: "37.5"
      }
    ]
  },
  cutConfig: {
    "morningWeight": "77.5",
    "nightWeight": "79",
    "currentFatPercentage": "15",
    "targetFatPercentage": "7",
    "leanMassGain": "1",
    "rhythm": "weekly",
    "time": 17,
    "skinFolds": [
        {
            "name": "Suprailiaco",
            "current": "22",
            "target": "10"
        },
        {
            "name": "Aductor",
            "current": "15",
            "target": "7"
        }
    ],
    "perimeters": [
        {
            "name": "Cintura",
            "current": "85",
            "target": "78"
        },
        {
            "name": "Biceps",
            "current": "40",
            "target": "40"
        }
    ]
  }
};

// Time/Rhythm constants
const TIME_UNITS = {
  daily: "days",
  weekly: "weeks",
  monthly: "months",
};

const RHYTHM_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

/**
 * Format number to fixed decimals
 */
const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return Number(value).toFixed(decimals);
};

/**
 * CalendarTables Component
 * 
 * Displays calendar tables with projected body composition values.
 */
export default function CalendarTables() {
  // Get config based on goal
  const config = MOCK_CONFIG.goal === "bulk" 
    ? MOCK_CONFIG.bulkConfig 
    : MOCK_CONFIG.cutConfig;

  const goalLabel = MOCK_CONFIG.goal === "bulk" ? "Bulk" : "Cut";
  const timeUnit = config?.rhythm ? TIME_UNITS[config.rhythm] : "weeks";
  const rhythmLabel = config?.rhythm ? RHYTHM_LABELS[config.rhythm] : "Weekly";

  // Calculate all parameters
  const calculatedParams = useMemo(() => {
    if (!config) return null;
    return calculateAllParameters(config);
  }, [config]);

  // Generate calendar lines
  const calendarLines = useMemo(() => {
    if (!config || !calculatedParams) return [];
    return generateCalendarLines(config, calculatedParams);
  }, [config, calculatedParams]);

  // Organize lines by month
  const calendarMonths = useMemo(() => {
    if (!calendarLines || calendarLines.length === 0) return [];
    return organizeLinesByMonth(calendarLines, config?.rhythm || "weekly");
  }, [calendarLines, config?.rhythm]);

  if (!config) {
    return (
      <div className="text-center py-12 text-gray-500">
        No configuration data available.
      </div>
    );
  }

  if (calendarMonths.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">No calendar data to display.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* ============================================ */}
      {/* CONFIGURATION PARAMETERS SECTION            */}
      {/* ============================================ */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
          <h3 className="text-sm font-semibold text-blue-800">{goalLabel} Configuration Parameters</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {/* Weight */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Morning Weight</p>
              <p className="text-sm font-semibold text-gray-900">{config.morningWeight} <span className="text-gray-400 font-normal">kg</span></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Night Weight</p>
              <p className="text-sm font-semibold text-gray-900">{config.nightWeight} <span className="text-gray-400 font-normal">kg</span></p>
            </div>
            
            {/* Body Composition */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Current Fat %</p>
              <p className="text-sm font-semibold text-gray-900">{config.currentFatPercentage} <span className="text-gray-400 font-normal">%</span></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Target Fat %</p>
              <p className="text-sm font-semibold text-gray-900">{config.targetFatPercentage} <span className="text-gray-400 font-normal">%</span></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Lean Mass Gain</p>
              <p className="text-sm font-semibold text-gray-900">{config.leanMassGain} <span className="text-gray-400 font-normal">kg</span></p>
            </div>
            
            {/* Timing */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Rhythm</p>
              <p className="text-sm font-semibold text-gray-900">{rhythmLabel}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Duration</p>
              <p className="text-sm font-semibold text-gray-900">{config.time} <span className="text-gray-400 font-normal">{timeUnit}</span></p>
            </div>

            {/* Skinfolds */}
            {config.skinFolds?.map((fold, index) => (
              <div key={`sf-${index}`} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{fold.name}</p>
                <p className="text-sm font-semibold text-gray-900">
                  {fold.current} → {fold.target} <span className="text-gray-400 font-normal">mm</span>
                </p>
              </div>
            ))}

            {/* Perimeters */}
            {config.perimeters?.map((perimeter, index) => (
              <div key={`pm-${index}`} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">{perimeter.name}</p>
                <p className="text-sm font-semibold text-gray-900">
                  {perimeter.current} → {perimeter.target} <span className="text-gray-400 font-normal">cm</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* CALCULATED PARAMETERS SECTION               */}
      {/* ============================================ */}
      {calculatedParams && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800">Calculated Parameters</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {/* Weight Targets */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Body Weight Target</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.bodyWeightTarget)} <span className="text-gray-400 font-normal">kg</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Lean Mass Target</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.leanMassTarget)} <span className="text-gray-400 font-normal">kg</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Current Lean Mass</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.currentLeanMass)} <span className="text-gray-400 font-normal">kg</span></p>
              </div>

              {/* Body Fat */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Current Body Fat</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.currentBodyFat)} <span className="text-gray-400 font-normal">kg</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Body Fat at End</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.bodyFatEnd)} <span className="text-gray-400 font-normal">kg</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Fat Difference</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.fatDifference)} <span className="text-gray-400 font-normal">kg</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Fat % Difference</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.fatPercentageDifference)} <span className="text-gray-400 font-normal">%</span></p>
              </div>

              {/* Ratios */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Lean Mass Ratio</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.leanMassRatio, 4)} <span className="text-gray-400 font-normal">kg/{timeUnit.slice(0, -1)}</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Fat Diff Ratio</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.fatDifferenceRatio, 4)} <span className="text-gray-400 font-normal">kg/{timeUnit.slice(0, -1)}</span></p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Weight Diff/Rhythm</p>
                <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.weightDifferenceRhythm, 4)} <span className="text-gray-400 font-normal">kg/{timeUnit.slice(0, -1)}</span></p>
              </div>

              {/* Measurement Ratios */}
              {config.skinFolds?.[0]?.name && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">{config.skinFolds[0].name} Ratio</p>
                  <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.firstParamRatio, 4)} <span className="text-gray-400 font-normal">mm/{timeUnit.slice(0, -1)}</span></p>
                </div>
              )}
              {config.skinFolds?.[1]?.name && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">{config.skinFolds[1].name} Ratio</p>
                  <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.secondParamRatio, 4)} <span className="text-gray-400 font-normal">mm/{timeUnit.slice(0, -1)}</span></p>
                </div>
              )}
              {config.perimeters?.[0]?.name && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">{config.perimeters[0].name} Ratio</p>
                  <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.thirdParamRatio, 4)} <span className="text-gray-400 font-normal">cm/{timeUnit.slice(0, -1)}</span></p>
                </div>
              )}
              {config.perimeters?.[1]?.name && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">{config.perimeters[1].name} Ratio</p>
                  <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.fourthParamRatio, 4)} <span className="text-gray-400 font-normal">cm/{timeUnit.slice(0, -1)}</span></p>
                </div>
              )}

              {/* Coefficient */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">SF/PM Rhythm Coef</p>
                <p className="text-sm font-semibold text-gray-900">{calculatedParams.skinfoldPerimeterRhythmCoefficient}</p>
              </div>

              {/* Energy (if available) */}
              {calculatedParams.totalCaloricDeficit !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Total Caloric Δ</p>
                  <p className="text-sm font-semibold text-gray-900">{formatNumber(calculatedParams.totalCaloricDeficit, 0)} <span className="text-gray-400 font-normal">kcal</span></p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* CALENDAR TABLES SECTION                     */}
      {/* ============================================ */}
      <h2 className="text-lg font-semibold text-gray-900">{MOCK_CONFIG.goal === "bulk" ? "Bulking" : "Cutting"} Calendar</h2>
      
      {calendarMonths.map((month, monthIndex) => (
        <div key={monthIndex} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          {/* Month Header */}
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">{month.name}</h3>
          </div>
          
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Period</th>
                  {config.skinFolds?.[0]?.name && (
                    <th className="px-3 py-2 text-right font-medium text-gray-500">
                      {config.skinFolds[0].name}
                      <span className="text-gray-400 ml-1">(mm)</span>
                    </th>
                  )}
                  {config.skinFolds?.[1]?.name && (
                    <th className="px-3 py-2 text-right font-medium text-gray-500">
                      {config.skinFolds[1].name}
                      <span className="text-gray-400 ml-1">(mm)</span>
                    </th>
                  )}
                  {config.perimeters?.[0]?.name && (
                    <th className="px-3 py-2 text-right font-medium text-gray-500">
                      {config.perimeters[0].name}
                      <span className="text-gray-400 ml-1">(cm)</span>
                    </th>
                  )}
                  {config.perimeters?.[1]?.name && (
                    <th className="px-3 py-2 text-right font-medium text-gray-500">
                      {config.perimeters[1].name}
                      <span className="text-gray-400 ml-1">(cm)</span>
                    </th>
                  )}
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    Fat %
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    AM Weight
                    <span className="text-gray-400 ml-1">(kg)</span>
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    PM Weight
                    <span className="text-gray-400 ml-1">(kg)</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {month.lines.map((line, lineIndex) => (
                  <tr 
                    key={lineIndex} 
                    className={`border-b border-gray-100 last:border-0 ${
                      lineIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    <td className="px-3 py-2 text-gray-600 font-medium">
                      {line.periodLabel}
                    </td>
                    {config.skinFolds?.[0]?.name && (
                      <td className="px-3 py-2 text-right text-gray-900 font-mono">
                        {formatNumber(line.firstParam, 1)}
                      </td>
                    )}
                    {config.skinFolds?.[1]?.name && (
                      <td className="px-3 py-2 text-right text-gray-900 font-mono">
                        {formatNumber(line.secondParam, 1)}
                      </td>
                    )}
                    {config.perimeters?.[0]?.name && (
                      <td className="px-3 py-2 text-right text-gray-900 font-mono">
                        {formatNumber(line.thirdParam, 1)}
                      </td>
                    )}
                    {config.perimeters?.[1]?.name && (
                      <td className="px-3 py-2 text-right text-gray-900 font-mono">
                        {formatNumber(line.fourthParam, 1)}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right text-gray-900 font-mono">
                      {formatNumber(line.fatPercentage, 1)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-900 font-mono">
                      {formatNumber(line.morningWeight, 1)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-900 font-mono">
                      {formatNumber(line.nightWeight, 1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}